import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Spinner } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import AddDataModal from '@/features/datasets/components/AddDataModal';
import GenerateDataWithAIModal from '@/features/datasets/components/DataCollection/GenerateDataWithAI/GenerateDataWithAIModal';
import RetrievalAgentModal from '@/features/datasets/components/DataCollection/RetrievalAgent/RetrievalAgentModal';
import LibraryPickModal from '@/features/datasets/components/LibraryPickModal';
import { runAIGenerationDataset } from '@/features/datasets/utils/aiGenerationDataset';
import { runRetrievalAgent } from '@/features/datasets/utils/retrievalAgentDataset';
import DataCollectionPanel from '../dataCollection/DataCollectionPanel';
import useDataCollectionHydration from '../dataCollection/useDataCollectionHydration';
import { rejectionLabelNameFromFallback } from '../businessGoals/fallbackLabel';

/**
 * Wizard Step 2 — "Data Collection". Thin host around shared
 * `DataCollectionPanel` that owns Library / Hugging Face / Generate-with-AI /
 * Retrieval Agent entry points (kept here to avoid a circular import with AddDataModal).
 */
const DataCollectionStep = ({
    usecase,
    subGroup,
    taskName,
    form,
    onFormChange,
    onBack,
    onNext,
    stateCacheRef,
    /** Demo tour: Next shows busy while the tour turns the page. */
    demoLoading = false,
}) => {
    const { refreshUsecases, seedGenerationProgress, seedRetrievalAgentProgress } = useContext(GlobalContext);

    const [addDataSource, setAddDataSource] = useState(null);
    const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
    const [libraryPickerTarget, setLibraryPickerTarget] = useState(null);
    const [pendingLibraryPick, setPendingLibraryPick] = useState(null);
    const [pendingHuggingFacePick, setPendingHuggingFacePick] = useState(null);
    const [datasetOptions, setDatasetOptions] = useState([]);
    const [benchmarkOptions, setBenchmarkOptions] = useState([]);
    const [libraryOptionsLoading, setLibraryOptionsLoading] = useState(false);
    const [isGenerateWithAiOpen, setIsGenerateWithAiOpen] = useState(false);
    const [isRetrievalAgentOpen, setIsRetrievalAgentOpen] = useState(false);

    // Draft resume: rebuild the panel's state cache from the server-persisted
    // dataset_info docs before the panel first mounts (the in-memory cache
    // dies with the wizard, so without this the step starts from scratch).
    const hydrationReady = useDataCollectionHydration({
        usecase,
        taskType: form.taskType,
        stateCacheRef,
    });

    const refreshLibraryOptions = useCallback(() => {
        const clientId = usecase?.client_id;
        if (!clientId) return;
        setLibraryOptionsLoading(true);
        data_from_server_post(
            'datasets-and-benchmarks',
            { value: clientId },
            (result) => {
                const mapOpts = (items) =>
                    (items || []).map((item) => ({
                        value: item.value,
                        label: item.value,
                        task_type: item.task_type,
                        column_mappings: item.column_mappings,
                        extra_params_keys: item.extra_params_keys || [],
                        file_column_names: item.file_column_names || [],
                        data_creation_type: item.data_creation_type,
                    }));
                setDatasetOptions(mapOpts(result?.datasets));
                setBenchmarkOptions(mapOpts(result?.benchmarks));
                setLibraryOptionsLoading(false);
            },
            () => setLibraryOptionsLoading(false)
        );
    }, [usecase?.client_id]);

    // Fetch on mount so "Choose from your uploads" can be hidden when the
    // library is empty, and again whenever the picker opens (fresh list).
    useEffect(() => {
        refreshLibraryOptions();
    }, [refreshLibraryOptions]);
    useEffect(() => {
        if (libraryPickerOpen) refreshLibraryOptions();
    }, [libraryPickerOpen, refreshLibraryOptions]);
    const hasLibraryUploads = datasetOptions.length > 0 || benchmarkOptions.length > 0;

    const openLibraryPicker = (target = null) => {
        setLibraryPickerTarget(target);
        setLibraryPickerOpen(true);
    };

    const libraryPickerOptions =
        libraryPickerTarget === 'benchmark'
            ? benchmarkOptions
            : libraryPickerTarget === 'dataset'
              ? datasetOptions
              : [...datasetOptions, ...benchmarkOptions];

    if (!hydrationReady) {
        return (
            <div className="cw-step cw-step--data-collection cw-step--loading">
                <Spinner size="medium" />
            </div>
        );
    }

    const generateAiUsecase = usecase
        ? {
              ...usecase,
              is_multilabel: usecase.is_multilabel === true || form.classificationType === 'multi',
              rejection_label_name: form.fallbackLabel
                  ? rejectionLabelNameFromFallback(form.fallbackLabel)
                  : usecase.rejection_label_name ?? null,
          }
        : usecase;

    return (
        <>
            <DataCollectionPanel
                variant="wizard"
                usecase={usecase}
                taskName={taskName}
                form={form}
                onFormChange={onFormChange}
                onBack={onBack}
                onNext={onNext}
                stateCacheRef={stateCacheRef}
                pendingLibraryPick={pendingLibraryPick}
                onPendingLibraryPickConsumed={() => setPendingLibraryPick(null)}
                pendingHuggingFacePick={pendingHuggingFacePick}
                onPendingHuggingFacePickConsumed={() => setPendingHuggingFacePick(null)}
                onOpenLibrary={openLibraryPicker}
                hasLibraryUploads={hasLibraryUploads}
                onOpenHuggingFace={() => setAddDataSource('huggingface')}
                onOpenGenerateAi={() => setIsGenerateWithAiOpen(true)}
                onOpenRetrievalAgent={() => setIsRetrievalAgentOpen(true)}
                demoLoading={demoLoading}
            />

            <LibraryPickModal
                open={libraryPickerOpen}
                onClose={() => setLibraryPickerOpen(false)}
                loading={libraryOptionsLoading}
                options={libraryPickerOptions}
                kind={libraryPickerTarget === 'benchmark' ? 'benchmark' : 'dataset'}
                onPick={(selectedOptions) => {
                    setPendingLibraryPick({
                        picks: (selectedOptions || []).map((option) => ({
                            target:
                                libraryPickerTarget ||
                                (benchmarkOptions.some((o) => o.value === option.value)
                                    ? 'benchmark'
                                    : 'dataset'),
                            option,
                        })),
                    });
                    setLibraryPickerOpen(false);
                }}
            />

            <AddDataModal
                open={addDataSource === 'huggingface'}
                onClose={() => setAddDataSource(null)}
                source="huggingface"
                usecase={usecase}
                subGroup={subGroup}
                onHuggingFacePick={(pick) => {
                    setPendingHuggingFacePick(pick);
                    setAddDataSource(null);
                }}
            />

            <GenerateDataWithAIModal
                visible={isGenerateWithAiOpen}
                onCancel={() => setIsGenerateWithAiOpen(false)}
                usecase={generateAiUsecase}
                onGenerate={(payload) =>
                    runAIGenerationDataset({ usecase: generateAiUsecase, payload, seedGenerationProgress }).then((res) => {
                        setIsGenerateWithAiOpen(false);
                        refreshUsecases?.();
                        return res;
                    })
                }
            />

            <RetrievalAgentModal
                visible={isRetrievalAgentOpen}
                onCancel={() => setIsRetrievalAgentOpen(false)}
                usecase={usecase}
                onInitiate={(payload) =>
                    runRetrievalAgent({ usecase, payload, seedRetrievalAgentProgress }).then((res) => {
                        setIsRetrievalAgentOpen(false);
                        refreshUsecases?.();
                        return res;
                    })
                }
            />
        </>
    );
};

export default DataCollectionStep;
